import { useMutation } from "@apollo/client";
import discussedTopicSvg from "@static/img/topic-discussed.svg";
import undiscussedTopicSvg from "@static/img/topic-undiscussed.svg";
import { compact, toInteger, uniqBy } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import store from "store2";
import {
  AddPreviousMeetingTopicsToCurrentMeetingMutationMutation,
  AddPreviousMeetingTopicsToCurrentMeetingMutationMutationVariables,
  MarkPreviousMeetingTopicsAsDiscussedMutationMutation,
  MarkPreviousMeetingTopicsAsDiscussedMutationMutationVariables,
  MeetingViewMeetingNodeNewPageFragmentFragment,
  ToggleTopicStateMutationMutation,
  ToggleTopicStateMutationMutationVariables,
  TopicState,
} from "types/graphql-schema";

import addPreviousMeetingTopicsMutation from "@apps/meeting-new/graphql/add-previous-meeting-topics-mutation";
import markPreviousMeetingTopicsDiscussedMutation from "@apps/meeting-new/graphql/mark-previous-meeting-topics-discussed-mutation";
import toggleTopicStateMutation from "@apps/topic-cta/graphql/toggle-topic-state-mutation";
import { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Modal from "@components/modal/modal";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { getAssetUrl, removeCurrentYear } from "@helpers/helpers";
import { pluralize } from "@helpers/string";

const storageKey = "meeting-dismissed-undiscussed-topics";
const howManyDaysAreTheMeetingNotesPersisted = 7;

const UndiscussedTopicsBanner = ({
  meeting,
  onTopicsChanged,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  onTopicsChanged?: () => void;
}) => {
  const previousMeeting = meeting.previousMeeting;
  const dismissedMeetings: { id: number; time: number }[] =
    store(storageKey) || [];

  const isPreviousMeetingDismissed =
    previousMeeting &&
    dismissedMeetings.some(
      (dismissedMeeting) => dismissedMeeting.id === previousMeeting.id,
    );
  const [dismissed, setDismissed] = useState(isPreviousMeetingDismissed);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [discussedTopicIds, setDiscussedTopicIds] = useState<Set<number>>(
    new Set(),
  );

  useEffect(() => {
    setDismissed(Boolean(isPreviousMeetingDismissed));
  }, [isPreviousMeetingDismissed, previousMeeting?.id]);

  useEffect(() => {
    setDiscussedTopicIds((prev) => {
      if (prev.size === 0) return prev;
      const currentUndiscussedIds = new Set(
        compact(meeting.undiscussedPreviousMeetingTopics ?? []).map(
          (t) => t.id,
        ),
      );
      const next = new Set(
        [...prev].filter((id) => !currentUndiscussedIds.has(id)),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [meeting.undiscussedPreviousMeetingTopics]);

  const handleDismiss = () => {
    if (!previousMeeting) return;

    // remove old dismissed meetings to keep the storage somewhat cleaned.
    const pastDateThreshold =
      Date.now() - howManyDaysAreTheMeetingNotesPersisted * 24 * 60 * 60 * 1000;
    const updateDismissedMeetings = uniqBy(
      [
        ...dismissedMeetings,
        { id: previousMeeting.id, time: toInteger(Date.now() / 1000) },
      ],
      "id",
    ).filter(
      (dismissedMeeting) => dismissedMeeting.time * 1000 > pastDateThreshold,
    );
    store(storageKey, updateDismissedMeetings);
    setDismissed(true);
    setIsModalOpen(false);
  };

  const [addTopics, { loading: addLoading }] = useMutation<
    AddPreviousMeetingTopicsToCurrentMeetingMutationMutation,
    AddPreviousMeetingTopicsToCurrentMeetingMutationMutationVariables
  >(addPreviousMeetingTopicsMutation);

  const [markDiscussed, { loading: markLoading }] = useMutation<
    MarkPreviousMeetingTopicsAsDiscussedMutationMutation,
    MarkPreviousMeetingTopicsAsDiscussedMutationMutationVariables
  >(markPreviousMeetingTopicsDiscussedMutation);

  const [toggleTopicState] = useMutation<
    ToggleTopicStateMutationMutation,
    ToggleTopicStateMutationMutationVariables
  >(toggleTopicStateMutation);

  const undiscussedTopics = compact(
    meeting.undiscussedPreviousMeetingTopics ?? [],
  );

  const previousMeetingIsCompleted = moment().isAfter(
    moment(meeting.previousMeeting?.startDatetime),
    "minute",
  );

  const meetingIsTooFarInThePast = moment(meeting.startDatetime).isBefore(
    moment().subtract(howManyDaysAreTheMeetingNotesPersisted, "day"),
    "day",
  );
  if (
    meetingIsTooFarInThePast ||
    !previousMeeting ||
    dismissed ||
    undiscussedTopics.length === 0 ||
    !previousMeetingIsCompleted
  ) {
    return null;
  }

  const topicCount = undiscussedTopics.length;
  const isLoading = addLoading || markLoading;

  const label_ = `${topicCount} topic${topicCount !== 1 ? "s" : ""} ${pluralize(
    "was",
    topicCount,
  )}n't discussed last meeting`;

  const previousMeetingDate = previousMeeting?.startDatetime
    ? removeCurrentYear(moment(previousMeeting.startDatetime).format("ll"))
    : null;
  const modalLabel = previousMeetingDate
    ? `${label_} (${previousMeetingDate})`
    : label_;

  const handleAddAll = (position: "top" | "bottom") => {
    addTopics({
      variables: { meetingId: meeting.id, position },
      onCompleted: () => {
        successNotificationVar({
          title: "Topics from the last meeting were added.",
          timeout: 3000,
        });
        handleDismiss();
        onTopicsChanged?.();
      },
    });
  };

  const handleMarkAllDiscussed = () => {
    markDiscussed({
      variables: { meetingId: meeting.id },
      onCompleted: () => {
        successNotificationVar({
          title: "Topics from the last meeting were marked as discussed.",
          timeout: 3000,
        });
        handleDismiss();
      },
    });
  };

  const handleMarkTopicDiscussed = (topicId: number) => {
    setDiscussedTopicIds((prev) => new Set(prev).add(topicId));
    toggleTopicState({
      variables: { topicId, state: TopicState.ClosedToDiscussion },
      refetchQueries: ["getMeetingNewPageQuery"],
      onCompleted: () => {
        successNotificationVar({
          title: "Topic marked as discussed.",
          timeout: 3000,
        });
      },
    });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="inline-flex items-center gap-2 -ml-2 px-2 py-1 rounded-md tf-header-sm-light hover:bg-gray-100 transition-colors w-fit"
        aria-label={label_}
      >
        <span>{label_}</span>
        <TopicflowIcon type="arrowNarrowRight" className="h-4 w-4 shrink-0" />
      </button>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className="flex flex-col gap-5 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="tf-header-base-dark">{modalLabel}</div>
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="flex items-center justify-center h-6 w-6 shrink-0 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close"
            >
              <TopicflowIcon type="close" className="h-4 w-4" />
            </button>
          </div>
          <div className="flex flex-col gap-1">
            {undiscussedTopics.map(
              (topic) =>
                topic && (
                  <div key={topic.id} className="flex gap-2 items-center">
                    <button
                      onClick={() => handleMarkTopicDiscussed(topic.id)}
                      disabled={isLoading || discussedTopicIds.has(topic.id)}
                      className="flex items-center justify-center h-6 w-6 shrink-0 rounded hover:bg-gray-100 disabled:opacity-50"
                      aria-label={`Mark "${topic.title}" as discussed`}
                    >
                      <img
                        src={getAssetUrl(
                          discussedTopicIds.has(topic.id)
                            ? discussedTopicSvg
                            : undiscussedTopicSvg,
                        )}
                        className="h-4 w-4"
                      />
                    </button>
                    <span
                      className={`text-sm transition-all duration-300 ${
                        discussedTopicIds.has(topic.id)
                          ? "text-gray-400 line-through"
                          : "text-gray-800"
                      }`}
                    >
                      {topic.title}
                    </span>
                  </div>
                ),
            )}
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Button
              theme={buttonTheme.default}
              disabled={isLoading}
              size={ButtonSize.medium}
              onClick={() => handleAddAll("top")}
            >
              <TopicflowIcon
                type="alignTop"
                className="mr-2 h-4 w-4 text-gray-400"
              />
              Add all to top of this meeting
            </Button>
            <Button
              theme={buttonTheme.default}
              disabled={isLoading}
              size={ButtonSize.medium}
              onClick={() => handleAddAll("bottom")}
            >
              <TopicflowIcon
                type="alignBottom"
                className="mr-2 h-4 w-4 text-gray-400"
              />
              Add all to bottom of this meeting
            </Button>
            <Button
              theme={buttonTheme.default}
              disabled={isLoading}
              size={ButtonSize.medium}
              onClick={handleMarkAllDiscussed}
            >
              <TopicflowIcon
                type="check"
                className="mr-2 h-4 w-4 text-gray-400"
              />
              Mark all as discussed
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UndiscussedTopicsBanner;
